import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';

/**
 * Componente para seleccionar la posición de asignación de un jugador
 */
const PositionSelector = ({
  jugador,
  onConfirm,
  onCancel,
  loading = false,
  compact = false
}) => {
  const [selectedPosition, setSelectedPosition] = useState(null);

  // Mapeo de posiciones
  const posiciones = [
    { id: 1, nombre: 'POR', descripcion: 'Portero' },
    { id: 2, nombre: 'DEF', descripcion: 'Defensa' },
    { id: 3, nombre: 'MED', descripcion: 'Medio' },
    { id: 4, nombre: 'DEL', descripcion: 'Delantero' },
    { id: 5, nombre: 'LIB', descripcion: 'Líbero' }
  ];

  // Obtener posiciones naturales del jugador
  const posicionesNaturales = [];
  if (jugador.posicion_principal) {
    const pos = posiciones.find(p => p.nombre === jugador.posicion_principal);
    if (pos) posicionesNaturales.push(pos);
  }
  if (jugador.posicion_secundaria && jugador.posicion_secundaria !== jugador.posicion_principal) {
    const pos = posiciones.find(p => p.nombre === jugador.posicion_secundaria);
    if (pos) posicionesNaturales.push(pos);
  }

  const handleConfirm = () => {
    if (selectedPosition && onConfirm) {
      onConfirm(selectedPosition);
    }
  };

  if (compact) {
    // Versión compacta para mostrar inline
    return (
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '10px',
        borderRadius: '6px',
        border: '1px solid #dee2e6',
        minWidth: '200px'
      }}>
        {/* Mostrar posiciones naturales del jugador */}
        {posicionesNaturales.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <small style={{ color: '#6c757d', fontSize: '10px' }}>
              Natural: {posicionesNaturales.map(p => p.nombre).join('/')}
            </small>
          </div>
        )}

        {/* Selector de posición compacto */}
        <div className="d-flex flex-wrap gap-1 mb-2">
          {posiciones.map(posicion => {
            const esNatural = posicionesNaturales.some(p => p.id === posicion.id);
            return (
              <Button
                key={posicion.id}
                variant={selectedPosition === posicion.id ? 'primary' : (esNatural ? 'outline-success' : 'outline-secondary')}
                size="sm"
                onClick={() => setSelectedPosition(posicion.id)}
                style={{
                  fontSize: '10px',
                  minWidth: '35px',
                  padding: '2px 4px',
                  position: 'relative'
                }}
              >
                {posicion.nombre}
                {esNatural && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-3px',
                      right: '-3px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      borderRadius: '50%',
                      width: '10px',
                      height: '10px',
                      fontSize: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Posición natural del jugador"
                  >
                    ✓
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Botones de acción compactos */}
        <div className="d-flex gap-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
            disabled={loading}
            style={{ fontSize: '10px', padding: '2px 6px' }}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={handleConfirm}
            disabled={!selectedPosition || loading}
            style={{ fontSize: '10px', padding: '2px 6px' }}
          >
            {loading ? 'Asignando...' : 'Asignar'}
          </Button>
        </div>
      </div>
    );
  }

  // Versión normal para modales grandes
  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      padding: '15px',
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      marginTop: '10px'
    }}>
      <h6 style={{ marginBottom: '10px', color: '#495057' }}>
        Seleccionar posición para {jugador.nombre}
      </h6>

      {/* Mostrar posiciones naturales del jugador */}
      {posicionesNaturales.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          <small style={{ color: '#6c757d', fontSize: '12px' }}>
            Posiciones naturales: {posicionesNaturales.map(p => p.nombre).join(' / ')}
          </small>
        </div>
      )}

      {/* Selector de posición */}
      <Form.Group className="mb-3">
        <Form.Label style={{ fontSize: '14px', fontWeight: '500' }}>
          Posición de asignación:
        </Form.Label>
        <div className="d-flex flex-wrap gap-2">
          {posiciones.map(posicion => {
            const esNatural = posicionesNaturales.some(p => p.id === posicion.id);
            return (
              <Button
                key={posicion.id}
                variant={selectedPosition === posicion.id ? 'primary' : (esNatural ? 'outline-success' : 'outline-secondary')}
                size="sm"
                onClick={() => setSelectedPosition(posicion.id)}
                style={{
                  fontSize: '12px',
                  minWidth: '60px',
                  position: 'relative'
                }}
              >
                {posicion.nombre}
                {esNatural && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-5px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      borderRadius: '50%',
                      width: '12px',
                      height: '12px',
                      fontSize: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Posición natural del jugador"
                  >
                    ✓
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      </Form.Group>

      {/* Botones de acción */}
      <div className="d-flex gap-2 justify-content-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={onCancel}
          disabled={loading}
          style={{ fontSize: '12px' }}
        >
          Cancelar
        </Button>
        <Button
          variant="success"
          size="sm"
          onClick={handleConfirm}
          disabled={!selectedPosition || loading}
          style={{ fontSize: '12px' }}
        >
          {loading ? 'Asignando...' : 'Asignar'}
        </Button>
      </div>
    </div>
  );
};

export default PositionSelector;
